import { useCallback, useEffect, useMemo, useState } from 'react';
import { Table, Tbody, Td, Th, Thead, Tr } from '@chakra-ui/react';
import { countries as allCountriesList, TCountryCode } from 'countries-list';
import ReactApexChart from 'react-apexcharts';

import { getCurrentMonth } from '@/utils/date';
import {
    getInspectionsByOperatorByCountry,
    getInspectionsByOperatorCountry,
    InspectionsByOperatorByCountry,
    InspectionsByOperatorCountry,
} from '@/services/dashboardService';

import MonthSelector from './MonthSelector';
import './dashboard.css';
import { DataStatus } from './dataStatus';
import { LoadingSpinner } from './LoadingSpinner';
import chartColors from './chartColors';

type MarketLabel = string;

const getRandomInspectionTotal = (min = 1, max = 10) => Math.floor(Math.random() * (max - min + 1)) + min;

const USE_MOCK_DATA = false; // Set to true to use mock data, false to fetch real data from the API
const DEFAULT_MARKET_ORDER: MarketLabel[] = ['tc', '3rd'];
const MOCK_MARKETS: MarketLabel[] = ['tc', '3rd']; //, 'Spot', 'COA'];
const MARKET_COLOR_PALETTE = [
    chartColors.blue,
    chartColors.red,
    chartColors.green,
    chartColors.yellow,
    chartColors.darkblue,
    chartColors.darkred,
    chartColors.darkgreen,
    chartColors.orange,
    '#7c3aed',
    '#0ea5e9',
    '#f97316',
    '#14b8a6',
];

const getCountryName = (country: TCountryCode) => allCountriesList[country]?.name ?? country;
const normalizeMarket = (market?: string): MarketLabel => market?.trim() || '-';
const getMarketKey = (market?: string) => normalizeMarket(market).toLowerCase();

const getOrderedMarkets = (markets: MarketLabel[]) => {
    const uniqueMarkets = new Map<string, MarketLabel>();
    const marketPriorityMap = new Map(DEFAULT_MARKET_ORDER.map((market, index) => [market.toLowerCase(), index]));

    markets.forEach((market) => {
        const normalizedMarket = normalizeMarket(market);
        const marketKey = getMarketKey(normalizedMarket);

        if (!uniqueMarkets.has(marketKey)) {
            uniqueMarkets.set(marketKey, normalizedMarket);
        }
    });

    return Array.from(uniqueMarkets.values()).sort((a, b) => {
        const indexA = marketPriorityMap.get(getMarketKey(a));
        const indexB = marketPriorityMap.get(getMarketKey(b));

        if (indexA != null && indexB != null) {
            return indexA - indexB;
        }

        if (indexA != null) {
            return -1;
        }

        if (indexB != null) {
            return 1;
        }

        return a.localeCompare(b, undefined, { sensitivity: 'base' });
    });
};

const getMarketColor = (market: MarketLabel, orderedMarkets: MarketLabel[]) => {
    const marketIndex = orderedMarkets.findIndex((orderedMarket) => getMarketKey(orderedMarket) === getMarketKey(market));

    if (marketIndex === -1) {
        return chartColors.blue;
    }

    return MARKET_COLOR_PALETTE[marketIndex % MARKET_COLOR_PALETTE.length];
};

const MOCK_COUNTRY_CODES: TCountryCode[] = [
    'ID', 'SG', 'MY', 'AU', 'US', 'GB', 'CN', 'JP', 'KR', 'IN',
    'TH', 'VN', 'PH', 'BR', 'MX', 'CA', 'DE', 'FR', 'IT', 'ES',
    'NL', 'BE', 'NO', 'SE', 'DK', 'FI', 'PL', 'TR', 'AE', 'SA',
    'QA', 'ZA', 'EG', 'NG', 'KE', 'AR', 'CL', 'PE', 'NZ', 'RU',
    'UA', 'GR', 'PT', 'IE', 'CH', 'AT', 'CZ', 'HU', 'RO', 'BG',
];

const MOCK_TABLE_DATA: InspectionsByOperatorByCountry[] = MOCK_COUNTRY_CODES.flatMap((country) => (
    MOCK_MARKETS.flatMap((market) => (
        [0, 1].map((operatorIndex) => ({
            yearInspection: 2026,
            monthInspection: 4,
            country,
            shipOperatorId: `op-${country.toLowerCase()}-${market.toLowerCase()}-${operatorIndex + 1}`,
            shipOperatorName: `${getCountryName(country)} Operator ${market}-${operatorIndex + 1}`,
            totalInspection: getRandomInspectionTotal(1, 10),
            acceptedNominationInspectionProcess: 0,
            cancelledNominationInspectionProcess: 0,
            inProgressNominationInspectionProcess: 0,
            inProgressInspectionProcess: 0,
            finishedInspectionProcess: 0,
            finishedInspectionProcessNotYetInvoices: 0,
            market,
        }))
    ))
));

const MOCK_CHART_DATA: InspectionsByOperatorCountry[] = MOCK_COUNTRY_CODES.flatMap((country) => (
    MOCK_MARKETS.map((market) => ({
        yearInspection: 2026,
        monthInspection: 4,
        country,
        totalInspection: MOCK_TABLE_DATA
            .filter((item) => item.country === country && getMarketKey(item.market) === getMarketKey(market))
            .reduce((total, item) => total + item.totalInspection, 0),
        acceptedNominationInspectionProcess: 0,
        cancelledNominationInspectionProcess: 0,
        inProgressNominationInspectionProcess: 0,
        inProgressInspectionProcess: 0,
        finishedInspectionProcess: 0,
        finishedInspectionProcessNotYetInvoices: 0,
        market,
    }))
));

const ChartInspectionsByOperatorByCountry = () => {
    const { year: currentYear, month: currentMonth } = getCurrentMonth();

    const [countries, setCountries] = useState<string[]>([]);
    const [countryCodes, setCountryCodes] = useState<TCountryCode[]>([]);
    const [marketLabels, setMarketLabels] = useState<MarketLabel[]>([]);
    const [series, setSeries] = useState<{ name: string; data: number[] }[]>([]);
    const [dataChartStatus, setDataChartStatus] = useState<DataStatus>(DataStatus.LOADING);
    const [selectedCountry, setSelectedCountry] = useState<TCountryCode | null>(null);
    const [dataTable, setDataTable] = useState<InspectionsByOperatorByCountry[]>([]);
    const [dataTableStatus, setDataTableStatus] = useState<DataStatus>(DataStatus.EMPTY);
    const [month, setMonth] = useState<number>(currentMonth);
    const [year, setYear] = useState<number>(currentYear);

    const transformData = (originalData: InspectionsByOperatorCountry[]) => {
        const groupedData = new Map<TCountryCode, Map<string, { marketLabel: MarketLabel; totalInspection: number }>>();

        originalData.forEach((operatorCountry) => {
            const marketLabel = normalizeMarket(operatorCountry.market);
            const marketKey = getMarketKey(operatorCountry.market);
            const currentCountryMarkets = groupedData.get(operatorCountry.country) ?? new Map<string, { marketLabel: MarketLabel; totalInspection: number }>();
            const currentMarketData = currentCountryMarkets.get(marketKey) ?? { marketLabel, totalInspection: 0 };

            currentMarketData.totalInspection += operatorCountry.totalInspection;
            currentCountryMarkets.set(marketKey, currentMarketData);
            groupedData.set(operatorCountry.country, currentCountryMarkets);
        });

        const nextMarketLabels = getOrderedMarkets(
            Array.from(groupedData.values()).flatMap((markets) => Array.from(markets.values()).map((market) => market.marketLabel))
        );

        const countryTotals = new Map<TCountryCode, number>();
        groupedData.forEach((markets, country) => {
            const total = Array.from(markets.values()).reduce((sum, market) => sum + market.totalInspection, 0);
            countryTotals.set(country, total);
        });

        const nextCountryCodes = Array.from(groupedData.keys()).sort((a, b) => {
            const totalA = countryTotals.get(a) ?? 0;
            const totalB = countryTotals.get(b) ?? 0;
            return totalB - totalA;
        });
        const nextCountries = nextCountryCodes.map((countryCode) => getCountryName(countryCode));
        const nextSeries = nextMarketLabels.map((market) => ({
            name: market,
            data: nextCountryCodes.map((countryCode) => groupedData.get(countryCode)?.get(getMarketKey(market))?.totalInspection ?? 0),
        }));

        return { nextCountryCodes, nextCountries, nextSeries, nextMarketLabels };
    };

    const getDataChart = useCallback(async (selectedMonth: number, selectedYear: number) => {
        setDataChartStatus(DataStatus.LOADING);

        try {
            const originalData = USE_MOCK_DATA
                ? MOCK_CHART_DATA
                : ((await getInspectionsByOperatorCountry(selectedMonth, selectedYear)).data as InspectionsByOperatorCountry[] | undefined) ?? [];

            if (originalData.length === 0) {
                setDataChartStatus(DataStatus.EMPTY);
                setCountries([]);
                setCountryCodes([]);
                setMarketLabels([]);
                setSeries([]);
                return;
            }

            const transformedData = transformData(originalData);
            setCountryCodes(transformedData.nextCountryCodes);
            setCountries(transformedData.nextCountries);
            setMarketLabels(transformedData.nextMarketLabels);
            setSeries(transformedData.nextSeries);

            const defaultSelection = originalData.find((item) => item.totalInspection > 0);
            setSelectedCountry((prev) => prev && transformedData.nextCountryCodes.includes(prev) ? prev : (defaultSelection?.country ?? null));
            setDataChartStatus(DataStatus.OK);
        } catch {
            setDataChartStatus(DataStatus.ERROR);
        }
    }, []);

    const getDataTable = useCallback(async (selectedMonth: number, selectedYear: number) => {
        if (selectedCountry == null) {
            setDataTable([]);
            setDataTableStatus(DataStatus.EMPTY);
            return;
        }

        setDataTableStatus(DataStatus.LOADING);

        try {
            const originalData = USE_MOCK_DATA
                ? MOCK_TABLE_DATA.filter((item) => item.country === selectedCountry)
                : ((((await getInspectionsByOperatorByCountry(selectedMonth, selectedYear, selectedCountry)).data as InspectionsByOperatorByCountry[] | undefined) ?? [])
                    .map((item) => ({ ...item, market: item.market ?? '-' })));

            if (originalData.length === 0) {
                setDataTable([]);
                setDataTableStatus(DataStatus.EMPTY);
                return;
            }

            setDataTable(originalData);
            setDataTableStatus(DataStatus.OK);
        } catch {
            setDataTable([]);
            setDataTableStatus(DataStatus.ERROR);
        }
    }, [selectedCountry]);

    useEffect(() => {
        void getDataChart(month, year);
    }, [getDataChart, month, year]);

    useEffect(() => {
        void getDataTable(month, year);
    }, [getDataTable, month, year]);

    const marketTotals = useMemo(() => (
        Object.fromEntries(series.map((item) => [item.name, item.data.reduce((total, value) => total + value, 0)]))
    ), [series]);

    const groupedTableData = useMemo(() => {
        const grouped = new Map<string, { label: MarketLabel; items: InspectionsByOperatorByCountry[] }>();

        dataTable.forEach((item) => {
            const marketLabel = normalizeMarket(item.market);
            const marketKey = getMarketKey(item.market);
            const currentGroup = grouped.get(marketKey) ?? { label: marketLabel, items: [] };

            currentGroup.items.push(item);
            grouped.set(marketKey, currentGroup);
        });

        return Array.from(grouped.entries())
            .sort(([marketKeyA], [marketKeyB]) => {
                const indexA = marketLabels.findIndex((market) => getMarketKey(market) === marketKeyA);
                const indexB = marketLabels.findIndex((market) => getMarketKey(market) === marketKeyB);

                if (indexA === -1 && indexB === -1) {
                    return marketKeyA.localeCompare(marketKeyB, undefined, { sensitivity: 'base' });
                }

                if (indexA === -1) {
                    return 1;
                }

                if (indexB === -1) {
                    return -1;
                }

                return indexA - indexB;
            })
            .map(([, group]) => [group.label, group.items] as const);
    }, [dataTable, marketLabels]);

    const marketColorMap = useMemo(() => (
        Object.fromEntries(marketLabels.map((market) => [getMarketKey(market), getMarketColor(market, marketLabels)]))
    ), [marketLabels]);

    const chartOptions: ApexCharts.ApexOptions = {
        chart: {
            type: 'bar',
            stacked: true,
            toolbar: {
                show: false,
            },
            events: {
                dataPointSelection: (_event, _chart, opts) => {
                    if (opts.dataPointIndex < 0 || opts.seriesIndex < 0) {
                        return;
                    }

                    const nextCountry = countryCodes[opts.dataPointIndex];

                    if (nextCountry) {
                        setSelectedCountry(nextCountry);
                    }
                },
            },
        },
        plotOptions: {
            bar: {
                horizontal: true,
                barHeight: '55%',
                dataLabels: {
                    position: 'center',
                    hideOverflowingLabels: false,
                },
            },
        },
        dataLabels: {
            enabled: true,
            formatter: (value, opts) => {
                if (!value) {
                    return '';
                }

                const marketName = opts.w.config.series?.[opts.seriesIndex]?.name ?? 'Market';
                return `${marketName}: ${value}`;
            },
            offsetX: 0,
            style: {
                fontSize: '10px',
                fontWeight: 'bold',
            },
            background: {
                enabled: true,
                foreColor: '#111827',
                borderRadius: 4,
                padding: 4,
                opacity: 0.85,
            },
        },
        xaxis: {
            categories: countries,
            title: {
                text: 'Total inspections',
            },
        },
        legend: {
            position: 'top',
        },
        tooltip: {
            shared: true,
            intersect: false,
            y: {
                formatter: (value, opts) => {
                    const marketName = opts.w.config.series?.[opts.seriesIndex]?.name ?? 'Market';
                    return `${marketName}: ${value}`;
                },
            },
        },
        colors: marketLabels.map((market) => getMarketColor(market, marketLabels)),
    };

    return (
        <div className='card-full'>
            <div>
                <h2 className='card-title'>Inspections by Operator by Country & Market</h2>
                <MonthSelector month={month} year={year} onChange={(selectedMonth, selectedYear) => { setMonth(selectedMonth); setYear(selectedYear); }} />
                {dataChartStatus === DataStatus.LOADING && <LoadingSpinner />}
            </div>

            {(dataChartStatus === DataStatus.EMPTY || dataChartStatus === DataStatus.ERROR) && <div>No data to display</div>}
            {dataChartStatus === DataStatus.OK && series.length > 0 && countries.length > 0 && <>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
                    {series.map((item) => (
                        <div
                            key={item.name}
                            style={{
                                padding: '6px 10px',
                                borderRadius: '8px',
                                background: '#f4f4f4',
                                borderLeft: `4px solid ${marketColorMap[getMarketKey(item.name)] ?? chartColors.blue}`,
                                fontSize: '14px'
                            }}
                        >
                            <strong>{item.name}:</strong> {marketTotals[item.name] ?? 0}
                        </div>
                    ))}
                </div>

                <div className='chart-with-table-chart' style={{ overflow: 'auto', height: '450px' }}>
                    <ReactApexChart type='bar' options={chartOptions} series={series} height={Math.max(320, countries.length * 70)} />
                </div>

                <div className='chart-with-table-table'>
                    <Table>
                        <Thead>
                            <Tr>
                                <Th>Country</Th>
                                <Th>Market</Th>
                                <Th>Ship Operator Name</Th>
                                <Th textAlign='right'>Inspections</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {dataTableStatus === DataStatus.OK && groupedTableData.flatMap(([market, operators]) => {
                                const subtotal = operators.reduce((total, operator) => total + operator.totalInspection, 0);

                                return [
                                    <Tr key={`market-header-${market}`} style={{ backgroundColor: `${marketColorMap[getMarketKey(market)] ?? chartColors.blue}22` }}>
                                        <Td colSpan={3} fontWeight='bold' style={{ borderLeft: `4px solid ${marketColorMap[getMarketKey(market)] ?? chartColors.blue}` }}>Market: {market}</Td>
                                        <Td textAlign='right' fontWeight='bold'>{subtotal}</Td>
                                    </Tr>,
                                    ...operators.map((operator) => (
                                        <Tr key={`${operator.country}-${operator.shipOperatorId}-${operator.market ?? 'unknown'}`}>
                                            <Td>{getCountryName(operator.country)}</Td>
                                            <Td>{operator.market ?? '-'}</Td>
                                            <Td>{operator.shipOperatorName}</Td>
                                            <Td textAlign='right'>{operator.totalInspection}</Td>
                                        </Tr>
                                    )),
                                ];
                            })}
                            {dataTableStatus === DataStatus.EMPTY && <Tr>
                                <Td colSpan={4} textAlign='center'>Select a country in the chart to see all markets and ship operators.</Td>
                            </Tr>}
                            {dataTableStatus === DataStatus.LOADING && <Tr>
                                <Td colSpan={4} textAlign='center'>Loading...</Td>
                            </Tr>}
                            {dataTableStatus === DataStatus.ERROR && <Tr>
                                <Td colSpan={4} textAlign='center'>Could not get data.</Td>
                            </Tr>}
                        </Tbody>
                    </Table>
                </div>
            </>}
        </div>
    );
};

export default ChartInspectionsByOperatorByCountry;
